import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  Heading,
  Modal,
  ModalOverlay,
} from "react-aria-components";
import { useNavigate, useSearchParams } from "react-router";

import { useAppSelector } from "@/app/hooks";
import { readyAttachmentFromResource, resourceTypeForFile } from "@/features/chat/chatAttachments";
import { firstConversationShopCandidate, resolveConversationShopBindingId, workspaceConversationPath } from "@/features/chat/conversationShopScope";
import {
  cloudUploadErrorMessage,
  uploadConversationAttachment,
} from "@/features/chat/cloudResourceUploadClient";
import {
  flattenMyFilesResourcePages,
  useCreateMyFilesArchiveMutation,
  useCreateMyFilesConversationMutation,
  useDeleteMyFilesResourcesMutation,
  useGetMyFilesArchiveQuery,
  useGetMyFilesDetailQuery,
  useGetMyFilesResourceAccessUrlMutation,
  useGetMyFilesResourcesInfiniteQuery,
  useRenameMyFilesResourceMutation,
  useRetryMyFilesArchiveMutation,
  type MyFilesResourceTypeFilter,
} from "@/features/my-files/myFilesApi";
import type {
  MyFilesDetail,
  MyFilesResource,
} from "@/features/my-files/myFilesSchemas";
import {
  MY_FILES_SELECTION_LIMIT,
  canDeleteMyFilesSelection,
} from "@/features/my-files/myFilesSelection";
import { selectWorkspaceStoreSeedBindingId } from "@/features/settings/activeStoreSlice";
import { useGetMerchantStoreBindingsQuery } from "@/features/settings/storeManagementApi";
import { MerryButton } from "@/shared/ui/MerryButton";
import {
  MerryMyFilesPanel,
  type MerryMyFilesCatalog,
  type MerryMyFilesBrowserState,
  type MerryMyFilesFilter,
  type MerryMyFilesItem,
  type MerryMyFilesPanelState,
  type MerryMyFilesType,
} from "@/shared/ui/MerryMyFilesPanel";

const initialBrowserState = {
  isManaging: false,
  layout: "list",
  page: "browser",
} as const satisfies MerryMyFilesPanelState;

const sourceLabels: Readonly<Record<string, string>> = {
  conversation_attachment: "对话附件",
  creative_generation: "Merry 生成",
  generated: "Merry 生成",
  image_generation: "Merry 生成",
  imported: "第三方导入",
  system_task: "系统任务",
  user_upload: "我的文件上传",
  video_generation: "Merry 生成",
};

function formatBytes(sizeBytes: number) {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  const units = ["KB", "MB", "GB"] as const;
  let value = sizeBytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 1 }).format(value)} ${units[unitIndex]}`;
}

function formatCreatedAt(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "long",
    timeZone: "Asia/Shanghai",
  }).format(new Date(value));
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Shanghai",
  }).format(new Date(value));
}

function myFilesType(resource: MyFilesResource): MerryMyFilesType {
  if (resource.resourceType === "image") return "image";
  if (resource.resourceType === "video") return "video";
  const extension = resource.extension.toLocaleLowerCase();
  if (extension === ".pdf") return "pdf";
  if (extension === ".ppt" || extension === ".pptx") return "ppt";
  if (extension === ".xls" || extension === ".xlsx") return "xlsx";
  if (extension === ".doc" || extension === ".docx") return "doc";
  return "file";
}

function dimensionsLabel(detail: MyFilesDetail | undefined) {
  const width = detail?.dimensions.width;
  const height = detail?.dimensions.height;
  if (width == null || height == null) return "—";
  return `${Math.round(width)} x ${Math.round(height)} PX`;
}

function toPanelItem(
  resource: MyFilesResource,
  details: MyFilesDetail | undefined,
  accessUrls: Readonly<Record<string, string>>,
): MerryMyFilesItem {
  return {
    availableActions: resource.availableActions,
    capacityLabel: formatBytes(resource.sizeBytes),
    createdAtLabel: formatCreatedAt(resource.createdAt),
    description: details?.source?.excerpt ?? "来源对话暂不可用",
    dimensionsLabel: dimensionsLabel(details),
    extension: resource.extension,
    id: resource.resourceId,
    name: resource.displayName,
    preview: details?.preview.kind ?? (
      resource.resourceType === "file" ? "unsupported" : "media"
    ),
    previewSource: accessUrls[resource.resourceId],
    referenceSources:
      details?.references.flatMap((reference) => {
        const accessUrl = accessUrls[reference.resourceId];
        return accessUrl ? [accessUrl] : [];
      }) ?? [],
    revision: resource.revision,
    sourceConversationId: details?.source?.conversationId,
    sourceLabel: sourceLabels[resource.sourceKind] ?? "Merry 资源",
    timeLabel: formatTime(resource.createdAt),
    type: myFilesType(resource),
  };
}

function buildCatalog(items: ReadonlyArray<MerryMyFilesItem>, hasNextPage: boolean) {
  const sections: MerryMyFilesCatalog["sections"] = [
    {
      id: "images",
      items: items.filter((item) => item.type === "image"),
      label: "图片",
    },
    {
      id: "videos",
      items: items.filter((item) => item.type === "video"),
      label: "视频",
    },
    {
      id: "files",
      items: items.filter(
        (item) => item.type !== "image" && item.type !== "video",
      ),
      label: "文件",
    },
  ].filter((section) => section.items.length > 0);
  return {
    loadMoreLabel: hasNextPage ? "加载更多" : "已经到底了",
    sections,
  } satisfies MerryMyFilesCatalog;
}

function triggerBrowserDownload(url: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener noreferrer";
  anchor.style.display = "none";
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function errorCode(error: unknown) {
  if (!error || typeof error !== "object" || !("data" in error)) return "";
  const data = error.data;
  if (!data || typeof data !== "object" || !("error" in data)) return "";
  return typeof data.error === "string" ? data.error : "";
}

function actionErrorMessage(error: unknown, fallback: string) {
  switch (errorCode(error)) {
    case "cloud_resource_revision_conflict":
      return "文件已被其他成员更新，请按最新名称重试";
    case "cloud_archive_limit_exceeded":
      return "单次最多下载 100 个文件且总容量不能超过 5 GiB";
    case "cloud_storage_forbidden":
      return "你没有执行此操作的权限";
    case "cloud_storage_resource_not_found":
      return "文件已不存在或暂不可用";
    default:
      return fallback;
  }
}

export interface MyFilesViewProps {
  onClose: () => void;
}

export function MyFilesView({ onClose }: MyFilesViewProps) {
  const rootRef = useRef<HTMLElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uploadControllerRef = useRef<AbortController | null>(null);
  const organizationId = useAppSelector(
    (state) => state.authSession.actor?.activeOrganizationId ?? "",
  );
  const selectedBindingId = useAppSelector((state) =>
    selectWorkspaceStoreSeedBindingId(state, organizationId),
  );
  const storeBindings = useGetMerchantStoreBindingsQuery(organizationId, {
    skip: !organizationId,
  });
  const seedShopBindingId = storeBindings.currentData
    ? resolveConversationShopBindingId(
        selectedBindingId,
        storeBindings.currentData.bindings,
      )
    : "";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resourceIdFromUrl = searchParams.get("resource")?.trim() ?? "";
  const [browserState, setBrowserState] =
    useState<MerryMyFilesBrowserState>(initialBrowserState);
  const [filter, setFilter] = useState<MerryMyFilesFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<ReadonlyArray<string>>([]);
  const [accessUrls, setAccessUrls] = useState<Readonly<Record<string, string>>>({});
  const [actionError, setActionError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [archiveId, setArchiveId] = useState("");
  const [deleteCandidates, setDeleteCandidates] = useState<
    ReadonlyArray<MerryMyFilesItem>
  >([]);
  const requestedAccessIdsRef = useRef(new Set<string>());
  const pendingDeleteFocusRef = useRef<{
    deletedIds: ReadonlySet<string>;
    targetResourceId: string;
  } | null>(null);
  const shouldRestoreDeleteFocusRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchQuery.trim()),
      250,
    );
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const listQuery = useGetMyFilesResourcesInfiniteQuery(
    {
      organizationId,
      resourceType: filter as MyFilesResourceTypeFilter,
      search: debouncedSearch,
    },
    { skip: !organizationId },
  );
  const detailQuery = useGetMyFilesDetailQuery(
    { organizationId, resourceId: resourceIdFromUrl },
    { skip: !organizationId || !resourceIdFromUrl },
  );
  const { data: archiveData, refetch: refetchArchive } =
    useGetMyFilesArchiveQuery(
    { archiveId, organizationId },
    {
      skip: !archiveId || !organizationId,
    },
    );
  const [accessResource] = useGetMyFilesResourceAccessUrlMutation();
  const [renameResource] = useRenameMyFilesResourceMutation();
  const [deleteResources, deleteState] = useDeleteMyFilesResourcesMutation();
  const [createArchive] = useCreateMyFilesArchiveMutation();
  const [retryArchive] = useRetryMyFilesArchiveMutation();
  const [createConversation] = useCreateMyFilesConversationMutation();

  useEffect(
    () => () => {
      uploadControllerRef.current?.abort();
    },
    [organizationId],
  );

  const listData = listQuery.data;
  const detailData = detailQuery.data;
  const resources = useMemo(() => {
    const byId = new Map(
      flattenMyFilesResourcePages(listData?.pages).map((resource) => [
        resource.resourceId,
        resource,
      ]),
    );
    if (detailData?.resource) {
      byId.set(detailData.resource.resourceId, detailData.resource);
    }
    return [...byId.values()];
  }, [detailData, listData]);

  const detail = detailData?.resource;
  const panelItems = useMemo(
    () =>
      resources.map((resource) =>
        toPanelItem(
          resource,
          resource.resourceId === detail?.resourceId ? detail : undefined,
          accessUrls,
        ),
      ),
    [accessUrls, detail, resources],
  );
  const catalog = useMemo(
    () => buildCatalog(panelItems, listQuery.hasNextPage),
    [listQuery.hasNextPage, panelItems],
  );

  useEffect(() => {
    const previewIds = resources
      .filter((resource) => resource.resourceType !== "file")
      .map((resource) => resource.resourceId);
    for (const reference of detail?.references ?? []) {
      if (reference.previewKind === "media") previewIds.push(reference.resourceId);
    }
    const pendingIds = [...new Set(previewIds)].filter(
      (resourceId) =>
        !accessUrls[resourceId] &&
        !requestedAccessIdsRef.current.has(resourceId),
    );
    if (pendingIds.length === 0 || !organizationId) return;

    for (const resourceId of pendingIds) {
      requestedAccessIdsRef.current.add(resourceId);
      void accessResource({ organizationId, resourceId })
        .unwrap()
        .then((result) => {
          setAccessUrls((current) => ({
            ...current,
            [resourceId]: result.url,
          }));
        })
        .catch(() => {
          requestedAccessIdsRef.current.delete(resourceId);
        });
    }
  }, [accessResource, accessUrls, detail?.references, organizationId, resources]);

  useEffect(() => {
    const archive = archiveData?.archive;
    if (!archive) return;
    if (archive.status === "available" && archive.downloadUrl) {
      const timer = window.setTimeout(() => {
        triggerBrowserDownload(archive.downloadUrl ?? "");
        setArchiveId("");
        setSelectedIds([]);
      }, 0);
      return () => window.clearTimeout(timer);
    }
    if (archive.status === "queued" || archive.status === "processing") {
      const timer = window.setTimeout(() => {
        refetchArchive();
      }, 1_500);
      return () => window.clearTimeout(timer);
    }
  }, [archiveData?.archive, refetchArchive]);

  const archive = archiveData?.archive;
  const archiveIsFailed = archive?.status === "failed" || archive?.status === "expired";
  const archiveNotice =
    archive?.status === "queued"
      ? "正在排队准备压缩包…"
      : archive?.status === "processing"
        ? "正在打包所选文件…"
        : "";
  const panelState: MerryMyFilesPanelState = resourceIdFromUrl
    ? { fileId: resourceIdFromUrl, page: "detail" }
    : browserState;

  const setCanonicalResource = (resourceId: string) => {
    const next = new URLSearchParams(searchParams);
    if (resourceId) next.set("resource", resourceId);
    else next.delete("resource");
    setSearchParams(next, { replace: true });
  };

  const handleStateChange = (nextState: MerryMyFilesPanelState) => {
    const wasManaging = panelState.page === "browser" && panelState.isManaging;
    const exitsManaging =
      wasManaging && nextState.page === "browser" && !nextState.isManaging;
    if (exitsManaging) setSelectedIds([]);
    if (nextState.page === "browser") setBrowserState(nextState);
    setCanonicalResource(nextState.page === "detail" ? nextState.fileId : "");
  };

  const handleFilterChange = (nextFilter: MerryMyFilesFilter) => {
    setFilter(nextFilter);
    setSelectedIds([]);
  };

  const handleSearchChange = (nextQuery: string) => {
    setSearchQuery(nextQuery);
    setSelectedIds([]);
  };

  const handleRename = async (
    item: MerryMyFilesItem,
    displayNameBase: string,
  ) => {
    try {
      await renameResource({
        displayNameBase,
        expectedRevision: item.revision ?? 1,
        organizationId,
        resourceId: item.id,
      }).unwrap();
      setActionError("");
    } catch (error) {
      setActionError(actionErrorMessage(error, "文件名保存失败"));
      if (errorCode(error) === "cloud_resource_revision_conflict") {
        await listQuery.refetch();
        if (resourceIdFromUrl) await detailQuery.refetch();
      }
      throw error;
    }
  };

  const handleDownload = async (items: ReadonlyArray<MerryMyFilesItem>) => {
    if (items.length === 0) return;
    setActionError("");
    if (items.length === 1) {
      const item = items[0];
      if (!item) return;
      try {
        const result = await accessResource({
          download: true,
          organizationId,
          resourceId: item.id,
        }).unwrap();
        triggerBrowserDownload(result.url);
      } catch (error) {
        setActionError(actionErrorMessage(error, "下载链接生成失败"));
      }
      return;
    }

    try {
      const result = await createArchive({
        idempotencyKey: crypto.randomUUID(),
        organizationId,
        resourceIds: items.map((item) => item.id),
      }).unwrap();
      setArchiveId(result.archive.archiveId);
    } catch (error) {
      setActionError(actionErrorMessage(error, "压缩包创建失败"));
    }
  };

  const handleRetryArchive = async () => {
    if (!archive || archive.status !== "failed") return;
    try {
      await retryArchive({
        archiveId: archive.archiveId,
        expectedRevision: archive.revision,
        organizationId,
      }).unwrap();
      refetchArchive();
    } catch (error) {
      setActionError(actionErrorMessage(error, "压缩任务重试失败"));
    }
  };

  const handleChat = async (item: MerryMyFilesItem) => {
    if (!storeBindings.currentData) {
      setActionError(storeBindings.isError ? "店铺范围读取失败，请重试" : "正在读取店铺范围，请稍后重试");
      return;
    }
    const shopBindingId = resolveConversationShopBindingId(
      selectedBindingId,
      storeBindings.currentData.bindings,
    );
    if (shopBindingId === null) {
      setActionError("请先在顶部店铺菜单选择本次对话使用的店铺");
      return;
    }
    const resource = resources.find((candidate) => candidate.resourceId === item.id);
    if (!resource) {
      setActionError("文件信息已变化，请重新读取后再试");
      return;
    }
    try {
      const result = await createConversation({
        organizationId,
        resourceId: item.id,
        shopBindingId,
        title: `围绕 ${item.name} 的对话`,
      }).unwrap();
      navigate(workspaceConversationPath(result.conversationId, shopBindingId), {
        state: {
          conversationId: result.conversationId,
          initialAttachment: readyAttachmentFromResource(resource),
        },
      });
    } catch (error) {
      setActionError(actionErrorMessage(error, "新对话创建失败"));
    }
  };

  const handleUploadFile = async (file: File) => {
    if (!organizationId || file.size === 0 || isUploading) {
      setActionError(file.size === 0 ? "请选择非空文件" : "暂时无法上传文件");
      return;
    }
    const controller = new AbortController();
    uploadControllerRef.current = controller;
    setIsUploading(true);
    setActionError("");
    try {
      const uploaded = await uploadConversationAttachment(
        organizationId,
        file,
        resourceTypeForFile(file),
        { signal: controller.signal, sourceKind: "user_upload" },
      );
      if (uploadControllerRef.current !== controller || controller.signal.aborted) return;
      await listQuery.refetch();
      if (controller.signal.aborted) return;
      setCanonicalResource(uploaded.resourceId);
    } catch (error) {
      if (uploadControllerRef.current === controller && !controller.signal.aborted) {
        setActionError(cloudUploadErrorMessage(error));
      }
    } finally {
      if (uploadControllerRef.current === controller) {
        uploadControllerRef.current = null;
        setIsUploading(false);
      }
    }
  };

  const handleDeleteRequest = (items: ReadonlyArray<MerryMyFilesItem>) => {
    const deletedIds = new Set(items.map((item) => item.id));
    const visibleIds = rootRef.current
      ? [
          ...rootRef.current.querySelectorAll<HTMLElement>(
            "[data-my-files-layout-item]",
          ),
        ]
          .map((element) => element.dataset.myFilesLayoutItem ?? "")
          .filter(Boolean)
      : [];
    const orderedIds =
      visibleIds.length > 0
        ? visibleIds
        : panelItems.map((item) => item.id);
    const firstDeletedIndex = orderedIds.findIndex((id) =>
      deletedIds.has(id),
    );
    const remainingIds = orderedIds.filter((id) => !deletedIds.has(id));
    const targetResourceId =
      remainingIds.find(
        (id) => orderedIds.indexOf(id) > firstDeletedIndex,
      ) ??
      remainingIds.at(-1) ??
      "";
    pendingDeleteFocusRef.current = { deletedIds, targetResourceId };
    shouldRestoreDeleteFocusRef.current = false;
    setDeleteCandidates(items);
  };

  const confirmDelete = async () => {
    const resourcesById = new Map(
      resources.map((resource) => [resource.resourceId, resource]),
    );
    const selectedResources = deleteCandidates.flatMap((item) => {
      const resource = resourcesById.get(item.id);
      return resource ? [resource] : [];
    });
    if (!canDeleteMyFilesSelection(selectedResources)) {
      setActionError("所选文件中包含无删除权限的资源");
      setDeleteCandidates([]);
      return;
    }

    const deletingDetailId =
      panelState.page === "detail" &&
      deleteCandidates.some((item) => item.id === panelState.fileId)
        ? panelState.fileId
        : "";
    try {
      if (deletingDetailId) {
        setBrowserState(initialBrowserState);
        setCanonicalResource("");
        await new Promise<void>((resolve) =>
          window.requestAnimationFrame(() => resolve()),
        );
      }
      await deleteResources({
        items: deleteCandidates.map((item) => ({
          expectedRevision: item.revision ?? 1,
          resourceId: item.id,
        })),
        organizationId,
      }).unwrap();
      shouldRestoreDeleteFocusRef.current = true;
      const deletedIds = new Set(deleteCandidates.map((item) => item.id));
      setSelectedIds((current) => current.filter((id) => !deletedIds.has(id)));
      setDeleteCandidates([]);
      setActionError("");
    } catch (error) {
      if (deletingDetailId) setCanonicalResource(deletingDetailId);
      setActionError(actionErrorMessage(error, "文件删除失败"));
    }
  };

  useEffect(() => {
    const pending = pendingDeleteFocusRef.current;
    if (
      !pending ||
      !shouldRestoreDeleteFocusRef.current ||
      panelState.page !== "browser"
    )
      return;
    if (
      resourceIdFromUrl &&
      resources.some((resource) =>
        pending.deletedIds.has(resource.resourceId),
      )
    )
      return;

    let frame = 0;
    let attempts = 0;
    const restoreFocus = () => {
      const root = rootRef.current;
      if (!root) return;
      const item = [
        ...root.querySelectorAll<HTMLElement>("[data-my-files-layout-item]"),
      ].find(
        (element) =>
          element.dataset.myFilesLayoutItem === pending.targetResourceId,
      );
      const target = item?.querySelector<HTMLElement>(
        'button[aria-label^="查看 "]',
      );
      if (target) {
        target.focus();
        pendingDeleteFocusRef.current = null;
        shouldRestoreDeleteFocusRef.current = false;
        return;
      }
      attempts += 1;
      if (attempts < 60) {
        frame = window.requestAnimationFrame(restoreFocus);
        return;
      }
      const fallback = root.querySelector<HTMLElement>(
        '[data-testid="my-files-manage-toggle"], button[aria-label="关闭我的文件"]',
      );
      fallback?.focus();
      pendingDeleteFocusRef.current = null;
      shouldRestoreDeleteFocusRef.current = false;
    };
    frame = window.requestAnimationFrame(restoreFocus);
    return () => window.cancelAnimationFrame(frame);
  }, [panelState.page, resourceIdFromUrl, resources]);

  const requestError = listQuery.isError
    ? "文件列表暂时无法读取"
    : detailQuery.isError && panelState.page === "detail"
      ? "文件详情暂时无法读取"
      : archiveIsFailed
        ? archive?.status === "expired"
          ? "压缩包已过期，请重新下载"
          : "文件打包失败，可以重试"
        : actionError;

  return (
    <section
      className="my-files-page"
      data-figma-source-node-ids="719:105765,719:107077,719:109986,719:111108,719:112194,720:100306,720:101665,720:103035,720:107205,720:105227,720:106220"
      data-testid="my-files-page"
      ref={rootRef}
    >
      <input
        aria-label="选择上传文件"
        className="my-files-page__upload-input"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = "";
          if (file) void handleUploadFile(file);
        }}
        ref={uploadInputRef}
        tabIndex={-1}
        type="file"
      />
      <MerryMyFilesPanel
        catalog={catalog}
        data-testid="my-files-panel"
        errorMessage={requestError}
        filter={filter}
        isLoading={listQuery.isLoading || listQuery.isFetchingNextPage}
        isUploading={isUploading}
        noticeMessage={archiveNotice}
        onChat={(item) => void handleChat(item)}
        onClose={onClose}
        onDelete={handleDeleteRequest}
        onDownload={(items) => void handleDownload(items)}
        onFilterChange={handleFilterChange}
        onLoadMore={
          listQuery.hasNextPage
            ? () => void listQuery.fetchNextPage()
            : undefined
        }
        onOpenConversation={(item) => {
          if (item.sourceConversationId) {
            navigate(
              workspaceConversationPath(
                item.sourceConversationId,
                firstConversationShopCandidate(seedShopBindingId),
              ),
            );
          }
        }}
        onRename={handleRename}
        onRetry={
          archive?.status === "failed"
            ? () => void handleRetryArchive()
            : panelState.page === "detail" && detailQuery.isError
              ? () => void detailQuery.refetch()
              : () => void listQuery.refetch()
        }
        onSearchQueryChange={handleSearchChange}
        onSelectedIdsChange={(ids) =>
          setSelectedIds([...new Set(ids)].slice(0, MY_FILES_SELECTION_LIMIT))
        }
        onStateChange={handleStateChange}
        onUpload={() => uploadInputRef.current?.click()}
        searchQuery={searchQuery}
        selectedIds={selectedIds}
        state={panelState}
      />

      <ModalOverlay
        className="my-files-delete-overlay"
        isDismissable={!deleteState.isLoading}
        isOpen={deleteCandidates.length > 0}
        onOpenChange={(open) => {
          if (!open && !deleteState.isLoading) {
            setDeleteCandidates([]);
            if (!shouldRestoreDeleteFocusRef.current)
              pendingDeleteFocusRef.current = null;
          }
        }}
      >
        <Modal className="my-files-delete-modal">
          <Dialog className="my-files-delete-dialog">
            <Heading slot="title">确认删除文件</Heading>
            <p>
              将永久删除所选 {deleteCandidates.length} 个文件，删除后无法恢复。
            </p>
            <div className="my-files-delete-dialog__actions">
              <MerryButton
                hierarchy="level1"
                isDisabled={deleteState.isLoading}
                onPress={() => {
                  pendingDeleteFocusRef.current = null;
                  setDeleteCandidates([]);
                }}
              >
                取消
              </MerryButton>
              <MerryButton
                hierarchy="level0"
                isDisabled={deleteState.isLoading}
                onPress={() => void confirmDelete()}
              >
                {deleteState.isLoading ? "正在删除…" : "确认删除"}
              </MerryButton>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </section>
  );
}
